import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { DragDropProvider } from '@dnd-kit/react'
import { useSortable, isSortable } from '@dnd-kit/react/sortable'
import { v4 as uuid } from 'uuid'
import { useTranslation } from 'react-i18next'

import TextField from '../../../../../../components/FormLegacy/FormFieldsV2/TextField'
import ToggleField from '../../../../../../components/FormLegacy/FormFieldsV2/ToggleField'
import Icon from '../../../../../../components/Icons'
import { getItemStyle, getListStyle, reorder } from '../../../helpers'

const SortableRoomItem = ({
  roomItem,
  index,
  isDisabled,
  errors,
  selectedRoom,
  isSubmitting,
  isGuided,
  openedBars,
  changeRoomItemParam,
  removeRoomItem,
  handleClickBar,
  handleQuestionsForItem,
}) => {
  const { t } = useTranslation(['checklists-legacy'])
  const { ref } = useSortable({ id: roomItem.id, index, disabled: isDisabled })

  return (
    <div ref={ref} style={getItemStyle()}>
      <div
        className={`label-caontainer ${
          errors.includes(`roomId-${selectedRoom.id}-objectId-${roomItem.id}`) ? 'is-error' : ''
        }`}
      >
        <div className="drag-and-drop__header">
          <div className="drag-and-drop__header__dots">
            <div className="drag-and-drop__header__dot" />
            <div className="drag-and-drop__header__dot" />
            <div className="drag-and-drop__header__dot" />
            <div className="drag-and-drop__header__dot" />
            <div className="drag-and-drop__header__dot" />
            <div className="drag-and-drop__header__dot" />
          </div>
        </div>
        <div className="drag-and-drop__body">
          <span style={{ fontWeight: 500, marginRight: 5 }}>{`${index + 1}. `}</span>
          <TextField
            name={`${roomItem.id}_${index}`}
            maxLength={50}
            placeholder={t('checklists-legacy:templateForm.newObjectPlaceholder')}
            value={roomItem.name}
            onChange={(e) => changeRoomItemParam(roomItem.id, 'name', e.target.value)}
            autoComplete="new-password"
            disabled={isSubmitting || isDisabled}
            isError={errors.includes(`roomId-${selectedRoom.id}-objectId-${roomItem.id}`)}
            onBlur={() => changeRoomItemParam(roomItem.id, 'name', roomItem.name.trim())}
          />
          {!isDisabled && (
            <React.Fragment>
              <div
                className="drag-and-drop__body__edit-icon-wrapper"
                onClick={() => removeRoomItem(roomItem.id)}
              >
                <Icon className="drag-and-drop__body__icon" icon="cross" viewBox={'-3 0 20 20'} />
              </div>
              {isGuided && (
                <div
                  className="drag-and-drop__body__arrow-icon-wrapper"
                  onClick={() => handleClickBar(roomItem.id)}
                >
                  <Icon
                    className={`drag-and-drop__body__arrow-icon ${
                      openedBars.includes(roomItem.id) ? 'is-open' : ''
                    }`}
                    icon="arrow"
                  />
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
      {isGuided && openedBars.includes(roomItem.id) && (
        <div className="body-container">
          <div className="single-body-row">
            <div className="default-text">
              {t('checklists-legacy:templateForm.descriptionInstructionLabel')}
            </div>
            <TextField
              name={`${roomItem.id}_${index}_description`}
              maxLength={255}
              placeholder={t('checklists-legacy:templateForm.descriptionPlaceholder')}
              value={roomItem.description}
              onChange={(e) => changeRoomItemParam(roomItem.id, 'description', e.target.value)}
              autoComplete="new-password"
              disabled={isSubmitting || isDisabled}
              isError={errors.includes(
                `roomId-${selectedRoom.id}-objectId-${roomItem.id}-description`,
              )}
              onBlur={() =>
                changeRoomItemParam(roomItem.id, 'description', roomItem.description.trim())
              }
            />
          </div>
          <div className="single-body-row">
            <ToggleField
              label={t('checklists-legacy:templateForm.requirePhoto')}
              name={`photo-toggle-${roomItem.id}`}
              value={roomItem.requirePhoto}
              onChange={() =>
                changeRoomItemParam(roomItem.id, 'requirePhoto', !roomItem.requirePhoto)
              }
              disabled={isSubmitting || isDisabled}
            />
            <ToggleField
              label={t('checklists-legacy:templateForm.conditionRating')}
              name={`condition-toggle-${roomItem.id}`}
              value={roomItem.requireRating}
              onChange={() =>
                changeRoomItemParam(roomItem.id, 'requireRating', !roomItem.requireRating)
              }
              disabled={isSubmitting || isDisabled}
            />
            <ToggleField
              label={t('checklists-legacy:templateForm.note')}
              name={`note-toggle-${roomItem.id}`}
              value={roomItem.requireNote}
              onChange={() =>
                changeRoomItemParam(roomItem.id, 'requireNote', !roomItem.requireNote)
              }
              disabled={isSubmitting || isDisabled}
            />
            {errors.includes(`roomId-${selectedRoom.id}-objectId-${roomItem.id}-toggles`) ? (
              <div className="error-text">
                {t('checklists-legacy:templateForm.atLeastOneOptionRequired')}
              </div>
            ) : null}
          </div>
          <div className="single-body-row low-padding">
            <div className="bold-text">{t('checklists-legacy:templateForm.advancedSettings')}</div>
          </div>
          {roomItem.questions &&
            roomItem.questions.map((questionObj, index) => (
              <div key={index} className="question-container">
                <span>{`${index + 1}. `}</span>
                <TextField
                  name={`${roomItem.id}_${index}`}
                  maxLength={100}
                  placeholder={t('checklists-legacy:templateForm.writeYourQuestionPlaceholder')}
                  value={questionObj.question}
                  onChange={(e) => handleQuestionsForItem(roomItem.id, index, e.target.value)}
                  autoComplete="new-password"
                  disabled={isSubmitting || isDisabled}
                />
                <div
                  className="edit-icon-wrapper"
                  onClick={() => handleQuestionsForItem(roomItem.id, index)}
                >
                  <Icon className="corss-icon" icon="cross" viewBox={'-3 0 20 20'} />
                </div>
              </div>
            ))}
          {(!roomItem.questions || roomItem.questions.length < 5) && (
            <div
              className="custom-question-button"
              onClick={() => handleQuestionsForItem(roomItem.id)}
            >
              <Icon icon="cross" viewBox="0 0 23 20" />
              {t('checklists-legacy:templateForm.addCustomQuestion')}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const ChecklistTemplateDetailsBody = ({
  templateMode,
  errors,
  selectedRoomItems,
  editModeList,
  selectedRoom,
  editData,
  errorMessage,
  changeRoomItemParam,
  setEditModeList,
  handleSubmit,
  isSubmitting,
  setEditData,
  isDisabled,
}) => {
  const { t } = useTranslation(['checklists-legacy'])
  const [openedBars, setOpenedBars] = useState([])

  const isGuided = editData.isGuided

  const removeRoomItem = (roomItemId) => {
    setEditData({
      ...editData,
      rooms: editData.rooms.map((room) =>
        room.id === selectedRoom.id
          ? { ...room, roomItems: room.roomItems.filter((item) => item.id !== roomItemId) }
          : room,
      ),
    })
  }

  const addNewRoomItem = () => {
    const newRoomItem = {
      description: '',
      hasIssue: null,
      id: uuid(),
      images: null,
      name: '',
      notes: null,
      questions: null,
      rating: null,
      requirePhoto: false,
      requireRating: false,
      revisionRequested: null,
    }

    const newData = editData.rooms.map((room) =>
      room.id === selectedRoom.id ? { ...room, roomItems: [...room.roomItems, newRoomItem] } : room,
    )
    setEditData({
      ...editData,
      rooms: newData,
    })
  }

  const handleEditRoomsValue = (roomId, inputName, value) => {
    const updatedRooms = editData.rooms.map((room) =>
      room.id === roomId ? { ...room, [inputName]: value } : room,
    )
    setEditData({ ...editData, rooms: updatedRooms })
  }

  const onDragEnd = (event) => {
    const { operation, canceled } = event
    if (canceled) return
    const { source } = operation
    if (!isSortable(source)) return
    const sourceIndex = source.initialIndex
    const targetIndex = source.index
    if (sourceIndex === targetIndex) return
    const reorderedListOfObjects = reorder(selectedRoomItems, sourceIndex, targetIndex)
    setEditData({
      ...editData,
      rooms: editData.rooms.map((room) =>
        room.id === selectedRoom.id ? { ...room, roomItems: reorderedListOfObjects } : room,
      ),
    })
  }

  const handleClickBar = (item) => {
    if (openedBars.includes(item)) {
      setOpenedBars(openedBars.filter((bar) => bar !== item))
    } else {
      setOpenedBars((prevBars) => [...prevBars, item])
    }
  }

  const handleQuestionsForItem = (roomItemId, questionIndex, questionNewValue) => {
    setEditData((prevEditData) => {
      const { rooms } = prevEditData
      const roomIndex = rooms.findIndex((room) => room.id === selectedRoom.id)

      if (roomIndex === -1) {
        // selectedRoom not found in rooms array, return previous state
        return prevEditData
      }

      const room = rooms[roomIndex]
      const roomItems = [...room.roomItems]
      const itemIndex = roomItems.findIndex((item) => item.id === roomItemId)

      if (itemIndex === -1) {
        // roomItemId not found in roomItems array, return previous state
        return prevEditData
      }

      const item = roomItems[itemIndex]

      let { questions } = item

      if (questionNewValue) {
        questions[questionIndex].question = questionNewValue
      } else if (typeof questionIndex === 'number') {
        questions.splice(questionIndex, 1)
      } else if (!questions) {
        questions = [{ question: '' }]
      } else {
        questions.push({ question: '' })
      }

      const updatedItem = { ...item, questions }
      const updatedRoomItems = [
        ...roomItems.slice(0, itemIndex),
        updatedItem,
        ...roomItems.slice(itemIndex + 1),
      ]
      const updatedRoom = { ...room, roomItems: updatedRoomItems }
      const updatedRooms = [
        ...rooms.slice(0, roomIndex),
        updatedRoom,
        ...rooms.slice(roomIndex + 1),
      ]
      return { ...prevEditData, rooms: updatedRooms }
    })
  }

  return (
    <div className="main-view-body">
      <div className="room-header">
        <div className="room-name">
          {editModeList.includes(`roomId-${selectedRoom.id}-locationName`) ? (
            <div className="big-columns">
              <TextField
                autoFocus
                maxLength={50}
                name={`${selectedRoom.id}`}
                placeholder={t('checklists-legacy:templateForm.locationNamePlaceholder')}
                value={editData.rooms.find((room) => room.id === selectedRoom.id).name}
                onChange={(e) => handleEditRoomsValue(selectedRoom.id, 'name', e.target.value)}
                autoComplete="new-password"
                disabled={isSubmitting}
                isError={errors.includes(`roomId-${selectedRoom.id}-locationName`)}
                errorLabel={
                  errors.includes(`roomId-${selectedRoom.id}-locationName`)
                    ? t('checklists-legacy:templateForm.requiredMax50')
                    : ''
                }
                onBlur={() =>
                  handleEditRoomsValue(
                    selectedRoom.id,
                    'name',
                    editData.rooms.find((room) => room.id === selectedRoom.id).name.trim(),
                  )
                }
              />
              <span
                className="cross-icon"
                onClick={() => handleEditRoomsValue(selectedRoom.id, 'name', '')}
              >
                <Icon icon="cross" viewBox="0 0 23 20" />
              </span>
            </div>
          ) : (
            <React.Fragment>
              {selectedRoom.name ? (
                <div className="element-value">{selectedRoom.name}</div>
              ) : (
                <div className="element-value placeholder">
                  {t('checklists-legacy:templateForm.locationNamePlaceholder')}
                </div>
              )}
              {!isDisabled && (
                <span
                  className="editPen-icon"
                  onClick={() =>
                    setEditModeList([...editModeList, `roomId-${selectedRoom.id}-locationName`])
                  }
                >
                  <Icon icon="editPen" viewBox="2 2 20 20" />
                </span>
              )}
            </React.Fragment>
          )}
        </div>
        <div className="guided-toggle-container">
          <ToggleField
            label={t('checklists-legacy:templateForm.guided')}
            name="guided-toggle"
            value={isGuided}
            onChange={() => setEditData({ ...editData, isGuided: !isGuided })}
            disabled={isSubmitting || isDisabled || templateMode === 'edit'}
          />
        </div>
      </div>
      <div className={`room-items-list-field ${isSubmitting ? 'is-disabled' : ''}`}>
        <DragDropProvider onDragEnd={onDragEnd}>
          <div style={getListStyle()}>
            {selectedRoomItems.map((roomItem, index) => (
              <SortableRoomItem
                key={roomItem.id}
                roomItem={roomItem}
                index={index}
                isDisabled={isDisabled}
                errors={errors}
                selectedRoom={selectedRoom}
                isSubmitting={isSubmitting}
                isGuided={isGuided}
                openedBars={openedBars}
                changeRoomItemParam={changeRoomItemParam}
                removeRoomItem={removeRoomItem}
                handleClickBar={handleClickBar}
                handleQuestionsForItem={handleQuestionsForItem}
              />
            ))}
          </div>
        </DragDropProvider>
        {!isDisabled && (
          <div className="add-object-button" onClick={addNewRoomItem}>
            <Icon icon="cross" viewBox="0 0 23 20" />
            {t('checklists-legacy:templateForm.addObject')}
          </div>
        )}
      </div>
      <div className="window-footer">
        <div className="error-message">{errorMessage}</div>
        <div className="footer-buttons">
          <button
            type="submit"
            className={`button button-black is-block ${isSubmitting ? 'is-loading' : ''}`}
            onClick={handleSubmit}
            disabled={isSubmitting || isDisabled}
          >
            {t('checklists-legacy:templateForm.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

ChecklistTemplateDetailsBody.propTypes = {
  templateMode: PropTypes.string,
  errors: PropTypes.array,
  selectedRoomItems: PropTypes.array,
  editModeList: PropTypes.array,
  selectedRoom: PropTypes.object,
  editData: PropTypes.object,
  errorMessage: PropTypes.string,
  changeRoomItemParam: PropTypes.func,
  setEditModeList: PropTypes.func,
  setEditData: PropTypes.func,
  handleSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  isDisabled: PropTypes.bool,
}

export default ChecklistTemplateDetailsBody
